import { Invoice } from '../types/invoice';

export interface PaymentSplit {
  payToFinancier: number;
  payToAstrafin: number;
  base: number;
  gst: number;
  tds: number;
  caseType:
    | 'Pre-GST (A)'
    | 'Post-GST (B)'
    | 'Post-TDS (C)'
    | 'Unassigned'
    | 'Pass-through Astrafin';
}

export function calculatePaymentSplit(invoice: Invoice): PaymentSplit {
  const base = invoice.baseRounded;
  const gst = invoice.gst12Rounded + invoice.gst18Rounded + invoice.gst28Rounded;
  const tds = invoice.tdsRounded;

  if (!invoice.assignedToLender) {
    return {
      payToFinancier: 0,
      payToAstrafin: invoice.grandTotalRounded,
      base, gst, tds,
      caseType: 'Unassigned',
    };
  }

  // Pass-through collection — Astrafin collects full amount on behalf of lender.
  // payToFinancier stays 0 here; the onward remittance to lender is tracked
  // separately in the lender_remittances table.
  if (invoice.collectionRoute === 'pass_through') {
    return {
      payToFinancier: 0,
      payToAstrafin: Math.round(base + gst - tds),
      base, gst, tds,
      caseType: 'Pass-through Astrafin',
    };
  }

  switch (invoice.fundingMode) {
    case 'A':
      return {
        payToFinancier: Math.round(base),
        payToAstrafin: Math.round(gst - tds),
        base, gst, tds,
        caseType: 'Pre-GST (A)',
      };
    case 'B':
      return {
        payToFinancier: Math.round(base + gst - tds),
        payToAstrafin: 0,
        base, gst, tds,
        caseType: 'Post-GST (B)',
      };
    case 'C':
      return {
        payToFinancier: Math.round(base + gst - tds),
        payToAstrafin: 0,
        base, gst, tds,
        caseType: 'Post-TDS (C)',
      };
    default:
      return {
        payToFinancier: Math.round(base + gst - tds),
        payToAstrafin: 0,
        base, gst, tds,
        caseType: 'Post-GST (B)',
      };
  }
}

/** Get the amount that would appear in Astrafin's bank statement for matching */
export function getAstrafInReceivable(invoice: Invoice): number {
  return calculatePaymentSplit(invoice).payToAstrafin;
}

/**
 * Amount Astrafin owes the lender on this invoice once the customer has paid.
 * - Direct route → 0 (customer paid lender directly)
 * - Pass-through → base (A) or base+gst−tds (B/C); for unassigned → 0
 */
export function getLenderRemittanceDue(invoice: Invoice): number {
  if (!invoice.assignedToLender) return 0;
  if (invoice.collectionRoute !== 'pass_through') return 0;
  const base = invoice.baseRounded;
  const gst = invoice.gst12Rounded + invoice.gst18Rounded + invoice.gst28Rounded;
  const tds = invoice.tdsRounded;
  if (invoice.fundingMode === 'A') return Math.round(base);
  return Math.round(base + gst - tds);
}
